import { useEffect, useRef, useState } from "react";
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import axios from 'axios';

import useDateEvents from "../../hooks/timeline/useDateEvents";

import useIsMobile from "../../hooks/useIsMobile";

import SelectStylist from "./Components/SelectStylist";

import useStylistsStore from "../../store/stylists";

import { useCalendarContext } from "../../contexts/Calendar";

import { dimensions, mockEvents,
  TimelineTableHead,
  TimelineTableDateEventBar,
  TimelineSpacer,
  TimelineFullDateEventsContainer,
  TimelineLegalHolidays,
  TimelineJewishHolidays,
  TimelineIconContainer,
  TimelineTableBodyContainer,
  TimelineTrackHeader,
  TimelineTrackHeaderScroll,
  TimelineTableBody,
  TableTimelineDisplay,
  TimelineEventBoard,
  TimelineHoursColumn,
  TimelineRibbon,
  TimelineSeeker,
  TimelineTrack,
  TimelineTrackTitle,


  PrintTimelineEvents,
  // PrintTimelineDisplay,
  PrintTimelineDay,
  // PrintTimelineHours,
  PrintTimelineHebEvents
 } from "./Common";

import { getYPos, roundMinutesToNearestPoint, roundToPointOfHourTime, getWeekDay, findEventInDate } from './helpers';

import WatchLaterIcon from '@mui/icons-material/WatchLater';

import PrintTimelineDisplay from "./Components/TimelineTile";

import PrintTimelineHours from "./Components/TimelineHours";

import TimelineContainer from "./Components/TimelineContainer";

import { BasicFlex } from "../../utils/Common";

import DownloadButton from "../../utils/utilities/Buttons/DownloadButton";
import Pickups from "./Components/Pickups";

const Container = styled.div`
  display: flex;
  gap: 1rem;
`;

const Calendar = styled.div`
  flex-grow: 1;
`;

const Actions = styled(BasicFlex)`
  width: auto;
  margin-left: auto;
  margin-right: 1rem;
`;


const TimelineComponent = ({hours = 24, divide = 2, curDate = {}, display = "vertical", children}) => {

  const activeStylists = useStylistsStore((state) => state.activeStylists);

  const tracks = activeStylists.filter(el => el.checked);

  const { isMobile } = useIsMobile();

  const { setting } = useCalendarContext() || {};

  let { view_density, display_hours, display_pickups } = setting?.data || {};

  let rowHeight = (+view_density || dimensions.timeline.hour.height);

  let beginShow = +display_hours?.start ? +display_hours?.start : 0;

  const dateEvents = useDateEvents({start_date: curDate, stylists: activeStylists.filter(el => el.checked).map(el => el.stylist_id)});

  const {day, year, month, scheduledEvents = [], dayOfWeek, fullDate, workingHours, filterByStylist, filterWorkingHours } = dateEvents;

  const [seekerPos, setSeekerPos] = useState(0);

  let curHour = new Date().getHours();
  let startHour = getYPos((curHour - beginShow), rowHeight);
  // let roundedTime = roundMinutesToNearestPoint(new Date());
  // let seekerPos = getYPos((curHour + (roundedTime / 100) - beginShow), rowHeight);

  useEffect(() => {
    const updateSeeker = () => {
      const time = new Date();
      const hour = time.getHours();
      const roundedTime = roundMinutesToNearestPoint(time, 1);
      setSeekerPos(getYPos((hour + (roundedTime / 100) - beginShow), rowHeight));
    };

    updateSeeker(); // run immediately on mount
    const id = setInterval(updateSeeker, 60 * 1000);
    return () => clearInterval(id);
  }, [beginShow, rowHeight]);


  const trackHeaderRef = useRef(null);
  const timelineDisplayRef = useRef(null);

  let scrollEvent = null;

  const touchStartX = useRef(0);
  const touchStartScrollLeft = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartScrollLeft.current = timelineDisplayRef?.current?.scrollLeft || 0;
  }

  const handleTouchMove = (e) => {
    const touchCurrentX = e.touches[0].clientX;
    const diff = touchStartX.current - touchCurrentX;
    
    timelineDisplayRef?.current?.scrollTo({
      left: touchStartScrollLeft.current + diff,
    });
    trackHeaderRef?.current?.scrollTo({
      left: touchStartScrollLeft.current + diff,
    });
  }


  const scrollHorizontal = (e) => {
  e.preventDefault();
  timelineDisplayRef?.current?.scrollBy({ top: 0, left: e.deltaY });
  trackHeaderRef?.current?.scrollBy({ top: 0, left: e.deltaY });
}

  return (
    <Container>
      <Calendar>
      <TimelineTableHead>
        <TimelineRibbon>
          <TimelineIconContainer>
            <WatchLaterIcon fontSize={isMobile ? "small" : "large"} />
          </TimelineIconContainer>
          <PrintTimelineDay day={dayOfWeek} date={fullDate} dayOfMonth={day} />
          <Actions gap="1rem">
            <DownloadButton url={`/appointments/generate/${encodeURIComponent(fullDate)}`} title="Print Daily Schedule" />
            <SelectStylist />
          </Actions>
        </TimelineRibbon>
      </TimelineTableHead>

      {!isMobile ? <TimelineTableDateEventBar>
        <TimelineFullDateEventsContainer>
          <TimelineLegalHolidays>
          </TimelineLegalHolidays>
          <TimelineJewishHolidays $reverse>
            <PrintTimelineHebEvents date={fullDate} key={fullDate.toISOString()} />
          </TimelineJewishHolidays>
        </TimelineFullDateEventsContainer>
      </TimelineTableDateEventBar> : null}

      <TimelineTrackHeader 
        onWheel={scrollHorizontal}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        <TimelineSpacer />
        <TimelineTrackHeaderScroll ref={trackHeaderRef}>
            {tracks.map(({stylist_id, full_name}, i) => (
              <TimelineTrackTitle key={stylist_id}>{full_name}</TimelineTrackTitle>
            ))}
        </TimelineTrackHeaderScroll>
      </TimelineTrackHeader>
      
      <TimelineContainer startHour={startHour}>
          
        <TimelineTableBody>
        <TimelineHoursColumn>
          <PrintTimelineHours />
        </TimelineHoursColumn>
        <TableTimelineDisplay $columns={tracks.length} ref={timelineDisplayRef}>
          <TimelineSeeker pos={seekerPos} />
          {tracks.map(({stylist_id, full_name}, i) => (
            <TimelineTrack key={full_name}>
              <TimelineEventBoard 
               id={`timeline${stylist_id}`}
              >
                <PrintTimelineEvents events={filterByStylist(stylist_id, scheduledEvents)} track={`timeline${stylist_id}`} />
                
              </TimelineEventBoard>

              <PrintTimelineDisplay 
                date={fullDate} 
                hours={hours} 
                divide={divide} 
                stylist={stylist_id} 
                id={`timeline${stylist_id}`} 
                track={i} 
                workingHours={filterWorkingHours(workingHours, stylist_id, getWeekDay(new Date(year, month, day), "long")?.toLowerCase())}
               />
            </TimelineTrack>
          ))}
         
        </TableTimelineDisplay>

        </TimelineTableBody>

      </TimelineContainer>
      </Calendar>
      {!isMobile && display_pickups ? <Pickups date={fullDate} /> : null}
    </Container> 
  )
}

export default TimelineComponent;